<template>
    <div :class="touchpoints.length > 0 ? 'p-0 table-card play-table-view' : ''">
        <div v-if="touchpoints.length === 0" class="text-center py-5">
            <h5>No touchpoints found</h5>
            <p v-if="activeFilterCount > 0">Try adjusting your filters to see more results.</p>
            <p v-else>There are no touchpoints in this play yet.</p>
        </div>
        <table v-else class="table table-width-responsive table-with-pagination mb-0">
            <thead>
                <tr class="table-header-row">
                    <th v-if="canEdit('playbooks')" class="table-column-1 d-table-cell bg-white column-sort-disabled" style="width: 30px;">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" value="" id="selectAll" :checked="allSelected" :indeterminate="someSelected" @change="toggleSelectAll($event)" ref="selectAllCheckbox" aria-label="Checkbox">
                        </div>
                    </th>
                    <th @click="handleSort('name')" class="d-table-cell bg-white" style="cursor: pointer;">
                        <div class="d-flex flex-row">
                            Name
                            <div class="d-flex flex-column">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sortKey === 'name' && sortOrder === 'asc'}" alt="">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sortKey === 'name' && sortOrder === 'des'}" alt="">
                            </div>
                        </div>
                    </th>
                    <th @click="handleSort('asset_type')" class="d-table-cell bg-white" style="cursor: pointer;">
                        <div class="d-flex flex-row">
                            Type
                            <div class="d-flex flex-column">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sortKey === 'asset_type' && sortOrder === 'asc'}" alt="">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sortKey === 'asset_type' && sortOrder === 'des'}" alt="">
                            </div>
                        </div>
                    </th>
                    <th class="d-none d-md-table-cell bg-white column-sort-disabled">
                        <div class="d-flex flex-row">
                            Status
                        </div>
                    </th>
                    <th @click="handleSort('launch_date')" class="d-none d-lg-table-cell bg-white" style="cursor: pointer;">
                        <div class="d-flex flex-row">
                            Launch Time
                            <div class="d-flex flex-column">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sortKey === 'launch_date' && sortOrder === 'asc'}" alt="">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sortKey === 'launch_date' && sortOrder === 'des'}" alt="">
                            </div>
                        </div>
                    </th>
                    <th @click="handleSort('audience')" class="d-none d-xl-table-cell bg-white" style="cursor: pointer;">
                        <div class="d-flex flex-row">
                            Target Audience
                            <div class="d-flex flex-column">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sortKey === 'audience' && sortOrder === 'asc'}" alt="">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sortKey === 'audience' && sortOrder === 'des'}" alt="">
                            </div>
                        </div>
                    </th>
                    <th class="d-none d-lg-table-cell bg-white column-sort-disabled">
                        <div class="d-flex flex-row">
                            Updated By
                        </div>
                    </th>
                    <th class="table-column-action column-sort-disabled d-table-cell bg-white pe-3" width="1"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(touchpoint, index) in touchpoints" :key="touchpoint.id" :class="`table-row-${index + 1}`" :style="canEdit('playbooks') ? { cursor: 'pointer' } : {}" @click="handleRowClick($event, touchpoint.id)">
                    <td v-if="canEdit('playbooks')" class="table-column-1 d-table-cell">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" :value="touchpoint.id" :id="'touchpoint-' + touchpoint.id" :checked="isSelected(touchpoint.id)" @change="toggleSelection(touchpoint.id)" @click.stop aria-label="Checkbox">
                        </div>
                    </td>
                    <td class="d-table-cell align-start text-start">
                        <div class="d-flex align-items-center">
                            <img :src="'/img/icons/avid-icons/' + (touchpoint.asset_type?.icon_class || 'email') + '.svg'" 
                                class="asset-type-icon icon me-2" 
                                data-bs-toggle="tooltip" 
                                :title="touchpoint.asset_type ? touchpoint.asset_type.name : 'Touchpoint type'">
                            <span>{{ touchpoint.name || 'Untitled Touchpoint' }}</span>
                        </div>
                    </td>
                    <td class="d-table-cell">
                        <span v-if="touchpoint.asset_type">{{ touchpoint.asset_type.name }}</span>
                        <span v-else class="text-muted">N/A</span>
                    </td>
                    <td class="d-none d-md-table-cell">
                        <span class="badge mx-0" 
                              :class="getTouchpointStatus(touchpoint).classes"
                              :data-bs-toggle="getTouchpointStatus(touchpoint).label === 'Incomplete' ? 'tooltip' : null"
                              :title="getTouchpointStatus(touchpoint).label === 'Incomplete' ? 'There are required inputs that are empty' : null">
                            {{ getTouchpointStatus(touchpoint).label }}
                        </span>
                    </td>
                    <td class="d-none d-lg-table-cell">
                        <span v-if="touchpoint.launch_date" class="text-muted">
                            {{ datetime(touchpoint.launch_date) }}
                        </span>
                        <span v-else class="text-muted">Not set</span>
                    </td>
                    <td class="d-none d-xl-table-cell">
                        <span v-if="touchpoint.segment && touchpoint.segment.name">
                            {{ touchpoint.segment.name }}
                        </span>
                        <span v-else class="text-muted">No audience</span>
                    </td>
                    <td class="d-none d-lg-table-cell">
                        <div class="user-info" v-if="touchpoint.updated_by_user">
                            <img class="avatar rounded-circle d-none d-xl-inline" :src="touchpoint.updated_by_user.image" />
                            {{ touchpoint.updated_by_user.first_name }} {{ touchpoint.updated_by_user.last_name ? touchpoint.updated_by_user.last_name.charAt(0) + '.' : '' }}
                        </div>
                        <span v-else class="text-muted">Unknown</span>
                    </td>
                    <td class="column-sort-disabled d-table-cell">
                        <div class="dropdown d-flex justify-content-center" @click.stop>
                            <button v-if="canEdit('playbooks') || canExport('playbooks')" class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" data-bs-auto-close="true" aria-expanded="false" :disabled="touchpoint.status == 'generating'" aria-label="Button">
                                <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" data-bs-toggle="tooltip" title="Actions" alt="">
                            </button>
                            <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                <a class="dropdown-item" href="#" @click.prevent="closeDropdownFromEvent($event); viewAssetTypeInfo(touchpoint)">
                                    <img class="icon me-2" src="/img/icons/avid-icons/deep-dive.svg" alt=""> Detailed View
                                </a>
                                <a v-if="canEdit('playbooks')" class="dropdown-item" href="#" @click.prevent="editTouchpoint(touchpoint, $event)">
                                    <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt=""> Edit
                                </a>
                                <a v-if="canEdit('playbooks')" class="dropdown-item" href="#" @click.prevent="closeDropdownFromEvent($event); touchpoint.status == 'generating' || isDraftTouchpoint(touchpoint) ? null : approveTouchpoint(touchpoint)" :class="{ 'disabled': touchpoint.status == 'generating' || isDraftTouchpoint(touchpoint) }">
                                    <img class="icon me-2" src="/img/icons/avid-icons/check-01.svg" alt=""> Approve
                                </a>
                                <a v-if="canExport('playbooks')" class="dropdown-item" href="#" @click.prevent="closeDropdownFromEvent($event); touchpoint.status == 'generating' || isDraftTouchpoint(touchpoint) ? null : deployTouchpoint(touchpoint)" :class="{ 'disabled': touchpoint.status == 'generating' || isDraftTouchpoint(touchpoint) }">
                                    <img class="icon me-2" src="/img/icons/avid-icons/rocket-02.svg" alt=""> Launch
                                </a>
                                <a v-if="canEdit('playbooks')" class="dropdown-item" href="#" @click.prevent="closeDropdownFromEvent($event); duplicateTouchpoint(touchpoint)">
                                    <img class="icon me-2" src="/img/icons/avid-icons/document-copy.svg" alt=""> Duplicate
                                </a>
                                <a v-if="canExport('playbooks')" class="dropdown-item" href="#" @click.prevent="closeDropdownFromEvent($event); reviewTouchpoint(touchpoint)">
                                    <img class="icon me-2" src="/img/icons/avid-icons/download.svg" alt=""> Review
                                </a>
                                <hr class="my-1" />
                                <a v-if="canEdit('playbooks')" class="dropdown-item text-danger" href="#" @click.prevent="closeDropdownFromEvent($event); deleteTouchpoint(touchpoint)">
                                    <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete
                                </a>
                            </div>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Edit Touchpoint Modal Component -->
        <edit-touchpoint-modal 
            :asset="selectedAssetForModal" 
            :status="selectedAssetStatus"
            :show="showModal" 
            :concepts="concepts"
            :client="client"
            @edit-asset="handleEditAsset"
            @close="closeModal" />

        <asset-type-info-modal :asset-type="selectedAssetType" :show="showAssetTypeModal" @close="closeAssetTypeModal" />
    </div>
</template>

<script>
import permissionsMixin from '../../mixins/permissions';
import filtersMixin from '../../mixins/filters';
import { usePlaybookStore } from '../../stores/playbook';
import { useClientStore } from '../../stores/client';
import { useNotifications } from '../../composables/useNotifications';
import AssetTypeInfoModal from './AssetTypeInfoModal.vue';
import EditTouchpointModal from './EditTouchpointModal.vue';

export default {
    name: 'TouchpointsTableView',
    mixins: [permissionsMixin, filtersMixin],
    components: { AssetTypeInfoModal, EditTouchpointModal },
    emits: ['sort-by-key', 'cards-state-changed', 'edit-asset', 'review-single-touchpoint'],
    props: {
        touchpoints: {
            type: Array,
            default: () => []
        },
        concepts: {
            type: Array,
            default: () => []
        },
        sortKey: {
            type: String,
            default: 'name'
        },
        sortOrder: {
            type: String,
            default: 'asc'
        },
        activeFilterCount: {
            type: Number,
            default: 0
        },
        selectedAssetIds: {
            type: Array,
            default: () => []
        }
    },
    setup() {
        const playbookStore = usePlaybookStore();
        const clientStore = useClientStore();
        const { showNotification } = useNotifications();
        return {
            playbookStore,
            clientStore,
            showNotification
        };
    },
    data() {
        return {
            selectedAssetId: null,
            showModal: false,
            selectedAssetType: null,
            showAssetTypeModal: false,
        };
    },
    computed: {
        client() {
            return this.clientStore.getClient;
        },
        playbook() {
            return this.playbookStore.getSelectedPlaybook;
        },
        selectedAssetForModal() {
            // Make this reactive to store updates by finding the asset from the store
            if (!this.selectedAssetId || !this.playbookStore.playbook_selected.assets) {
                return null;
            }
            return this.playbookStore.playbook_selected.assets.find(a => a.id === this.selectedAssetId) || null;
        },
        selectedAssetStatus() {
            if (!this.selectedAssetForModal) {
                return null;
            }
            return this.getTouchpointStatus(this.selectedAssetForModal);
        },
        allSelected() {
            if (this.touchpoints.length === 0) return false;
            return this.touchpoints.every(tp => this.selectedAssetIds.includes(tp.id));
        },
        someSelected() {
            if (this.touchpoints.length === 0) return false;
            const selectedCount = this.touchpoints.filter(tp => this.selectedAssetIds.includes(tp.id)).length;
            return selectedCount > 0 && selectedCount < this.touchpoints.length;
        }
    },
    mounted() {
        // Initialize tooltips
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

        // Add event listeners to hide tooltips on click and mouseleave
        for (var i = 0; i < tooltipTriggerList.length; i++) {
          (function(index) {
            tooltipTriggerList[index].addEventListener('click', function() {
              tooltipList[index].hide();
            });
            tooltipTriggerList[index].addEventListener('mouseleave', function() {
              tooltipList[index].hide();
            });
          })(i);
        }
    },
    methods: {
        isSelected(assetId) {
            return this.selectedAssetIds.includes(assetId);
        },
        toggleSelection(assetId) {
            const currentSelection = [...this.selectedAssetIds];
            const index = currentSelection.indexOf(assetId);
            
            if (index > -1) {
                currentSelection.splice(index, 1);
            } else {
                currentSelection.push(assetId);
            }
            
            this.$emit('cards-state-changed', currentSelection);
        },
        handleRowClick(event, assetId) {
            // Only toggle if user can edit playbooks
            if (!this.canEdit('playbooks')) {
                return;
            }
            
            // Check if the click was on the dropdown or its children
            const clickedElement = event.target;
            const dropdownContainer = clickedElement.closest('.dropdown');
            
            // If click was on dropdown, don't toggle
            if (dropdownContainer) {
                return;
            }
            
            // Toggle selection
            this.toggleSelection(assetId);
        },
        toggleSelectAll(event) {
            const isChecked = event.target.checked;
            let newSelection;
            
            if (isChecked) {
                // Select all touchpoints
                newSelection = [...new Set([...this.selectedAssetIds, ...this.touchpoints.map(tp => tp.id)])];
            } else {
                // Deselect all touchpoints in current list
                const currentIds = this.touchpoints.map(tp => tp.id);
                newSelection = this.selectedAssetIds.filter(id => !currentIds.includes(id));
            }
            
            this.$emit('cards-state-changed', newSelection);
        },
        toggleSelectAllProgrammatic() {
            // Programmatic version that can be called from parent component
            const allSelected = this.allSelected;
            let newSelection;
            
            if (allSelected) {
                // Deselect all touchpoints in current list
                const currentIds = this.touchpoints.map(tp => tp.id);
                newSelection = this.selectedAssetIds.filter(id => !currentIds.includes(id));
            } else {
                // Select all touchpoints
                newSelection = [...new Set([...this.selectedAssetIds, ...this.touchpoints.map(tp => tp.id)])];
            }
            
            this.$emit('cards-state-changed', newSelection);
        },
        handleSort(key) {
            this.$emit('sort-by-key', key);
        },
        getTouchpointStatus(touchpoint) {
            // Returns an object with label and CSS classes for the status badge
            if (touchpoint.source == null || this.isDraftTouchpoint(touchpoint)) {
                return { label: 'Incomplete', classes: 'bg-red bg-icon-warning' };
            } else if (touchpoint.deployed_at) {
                return { label: 'Launched', classes: 'bg-orange bg-icon-launched' };
            } else if (touchpoint.status == 'approved') {
                return { label: 'Approved', classes: 'bg-green bg-icon-ready' };
            } else if (touchpoint.status == 'generating') {
                return { label: 'Generating', classes: 'bg-blue bg-icon-generating' };
            } else {
                return { label: 'Ready', classes: 'bg-green bg-icon-ready' };
            }
        },
        isDraftTouchpoint(touchpoint) {
            // A touchpoint is in draft mode if it's missing any required field
            if (!touchpoint || !touchpoint.asset_type || !touchpoint.asset_type.inputs || !touchpoint.inputs) {
                return true;
            }

            // Get required inputs (inputs where optional is false or not set)
            const requiredInputs = touchpoint.asset_type.inputs.filter(input => !input.optional);

            // Helper function to check if a value is empty
            const isEmpty = (value) => {
                if (value === undefined || value === null) return true;
                if (typeof value === 'string') return value.trim() === '';
                if (Array.isArray(value)) {
                    if (value.length === 0) return true;
                    return value.every(item => {
                        if (item === null || item === undefined) return true;
                        if (typeof item === 'string') return item.trim() === '';
                        if (Array.isArray(item)) return isEmpty(item);
                        if (typeof item === 'object') {
                            return Object.keys(item).length === 0 || 
                                   Object.values(item).every(val => isEmpty(val));
                        }
                        return false;
                    });
                }
                return false;
            };

            // Helper function to check group inputs
            const checkGroupInputs = (groupInput, groupItems) => {
                if (!groupItems || !Array.isArray(groupItems)) return true;

                const groupRequiredInputs = touchpoint.asset_type.inputs.filter(
                    input => input.group_id === groupInput.id && !input.optional
                );

                for (const item of groupItems) {
                    if (!item) continue;

                    for (const requiredInput of groupRequiredInputs) {
                        if (!requiredInput || !requiredInput.placeholder) continue;

                        const value = item[requiredInput.placeholder];
                        if (isEmpty(value)) {
                            return true;
                        }
                    }
                }

                return false;
            };

            // Check each required input
            for (const requiredInput of requiredInputs) {
                if (!requiredInput || !requiredInput.placeholder) continue;

                if (requiredInput.group_id) {
                    continue;
                }

                const input = Array.isArray(touchpoint.inputs) 
                    ? touchpoint.inputs.find(input => input && input.placeholder === requiredInput.placeholder)
                    : null;

                if (!input) {
                    if (requiredInput.placeholder !== 'citations' && requiredInput.placeholder !== 'link') {
                        return true;
                    }
                    continue;
                }

                if (requiredInput.type === 'group') {
                    if (isEmpty(input.value) || checkGroupInputs(requiredInput, input.value)) {
                        return true;
                    }
                }
                else if (isEmpty(input.value)) {
                    if (requiredInput.placeholder !== 'citations' && requiredInput.placeholder !== 'link') {
                        return true;
                    }
                }
            }

            return false;
        },
        viewAssetTypeInfo(touchpoint) {
            this.selectedAssetType = touchpoint.asset_type;
            this.showAssetTypeModal = true;
        },
        editTouchpoint(touchpoint, event) {
            this.closeDropdownFromEvent(event);
            this.selectedAssetId = touchpoint.id;
            this.showModal = true;
        },
        closeDropdownFromEvent(event) {
            const toggle = event?.currentTarget?.closest('.dropdown')?.querySelector('[data-bs-toggle="dropdown"]');
            if (toggle && window.bootstrap?.Dropdown) {
                const instance = window.bootstrap.Dropdown.getInstance(toggle);
                instance?.hide();
            }
        },
        async approveTouchpoint(touchpoint) {
            try {
                const response = await window.axios.post(`/api/playbooks/${this.client.url}/assets/${touchpoint.id}/approve`);
                
                if (response.data && response.data.asset) {
                    this.playbookStore.updateAsset(response.data.asset.id, response.data.asset);
                    
                    this.showNotification({
                        text: '<strong>' + response.data.asset.name + "</strong> was successfully updated!"
                    });
                }
            } catch (error) {
                console.error('Error approving touchpoint:', error);
                
                this.showNotification({
                    text: 'Failed to update <strong>' + touchpoint.name + "</strong>. Please try again.",
                    type: 'error'
                });
            }
        },
        deployTouchpoint(touchpoint) {
            this.$emit('review-single-touchpoint', touchpoint);
        },
        async duplicateTouchpoint(touchpoint) {
            try {
                const duplicatedAsset = {
                    ...touchpoint,
                    name: touchpoint.name + ' (Copy)',
                    id: null,
                    status: 'draft',
                    approved_by: null,
                    approved_at: null,
                    source: 'manual',
                    source_details: 'Duplicated from existing touchpoint'
                };
                
                this.playbookStore.addAsset(duplicatedAsset);
                
                const response = await window.axios.post(`/api/playbooks/${this.client.url}/assets`, {
                    client_id: this.client.id,
                    playbook_id: touchpoint.playbook_id,
                    playbook_asset_type_id: touchpoint.playbook_asset_type_id,
                    name: duplicatedAsset.name,
                    description: touchpoint.description,
                    launch_date: touchpoint.launch_date,
                    inputs: touchpoint.inputs,
                    status: 'draft',
                    segment: touchpoint.segment,
                    destination_asset_id: touchpoint.destination_asset_id
                });
                
                if (response.data && response.data.asset) {
                    if (this.playbookStore.playbook_selected.assets) {
                        const optimisticIndex = this.playbookStore.playbook_selected.assets.findIndex(a => a.id === null && a.name === duplicatedAsset.name);
                        if (optimisticIndex >= 0) {
                            this.playbookStore.playbook_selected.assets.splice(optimisticIndex, 1);
                        }
                    }
                    
                    this.playbookStore.addAsset(response.data.asset);
                    
                    this.selectedAssetId = response.data.asset.id;
                    this.showModal = true;
                    
                    this.showNotification({
                        text: '<strong>' + response.data.asset.name + "</strong> was successfully created!"
                    });
                }
            } catch (error) {
                console.error('Error duplicating touchpoint:', error);
                
                if (this.playbookStore.playbook_selected.assets) {
                    const optimisticIndex = this.playbookStore.playbook_selected.assets.findIndex(a => a.id === null && a.name === duplicatedAsset.name);
                    if (optimisticIndex >= 0) {
                        this.playbookStore.playbook_selected.assets.splice(optimisticIndex, 1);
                    }
                }
                
                this.showNotification({
                    text: 'Your touchpoint failed to duplicate. Please try again.',
                    type: 'error'
                });
            }
        },
        reviewTouchpoint(touchpoint) {
            this.$emit('review-single-touchpoint', touchpoint);
        },
        async deleteTouchpoint(touchpoint) {
            Swal.fire({
                title: `Are you sure you want to delete "${touchpoint.name}"?`,
                text: "This action cannot be undone.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        await window.axios.delete(`/api/playbooks/${this.client.url}/assets/${touchpoint.id}`);
                        this.playbookStore.removeAsset(touchpoint.id);

                        this.showNotification({
                            text: '<strong>' + touchpoint.name + "</strong> was successfully deleted!"
                        });
                    } catch (error) {
                        console.error('Error deleting touchpoint:', error);
                        
                        this.showNotification({
                            text: 'Your touchpoint failed to delete. Please try again.',
                            type: 'error'
                        });
                    }
                }
            });
        },
        async handleEditAsset(asset) {
            try {
                const result = await this.playbookStore.updateAssetFromAPI(asset);

                if (result.success) {
                    this.$nextTick(()=>{
                        this.showNotification({
                            text: '<strong>' + asset.name + "</strong> was successfully updated!"
                        });
                    });
                }
            
                this.closeModal();
                
            } catch (error) {
                console.error('Error updating asset:', error);
                
                this.showNotification({
                    text: 'Failed to update <strong>' + asset.name + "</strong>. Please try again.",
                    type: 'error'
                });
            }
        },
        closeModal() {
            this.showModal = false;
            this.selectedAssetId = null;
        },
        closeAssetTypeModal() {
            this.showAssetTypeModal = false;
        }
    }
};
</script>

